import { useEffect, useMemo, useState } from "react";
import { useUserContext } from "sj-ui-toolkit/src/atlas/contexts";
import { getDirectoryClient } from "sj-ui-toolkit/src/client";
import { getIntegrationsClient } from "sj-ui-toolkit/src/client/svcClients";
import type { RecordCWERequest } from "sj-ui-toolkit/src/client/svcIntegrationsClient";
import { getContentClient, type LessonDetailsPublic } from "sj-ui-toolkit/src/client/svcContent";
import { useToast } from "sj-ui-toolkit/src/hooks";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import { Panel } from "primereact/panel";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { Button } from "primereact/button";
import type { DirectoryUserShape } from "@/api/assignments";

const DEFAULT_GIT_REPO = "securityjourney-frontend-monorepo";
const MIN_TEXT_LEN = 8;
const SHA_REGEX = /^[0-9a-f]{40}$/i;
const CWE_TAGS_PER_REQUEST = 100;

const randomHex40 = () =>
  Array.from({ length: 40 }, () => Math.floor(Math.random() * 16).toString(16)).join("");

const randomPrNumber = () => Math.floor(Math.random() * 1000) + 1;

const buildCweOptions = (
  cweDescriptions: Record<string, { Name?: string; Description?: string }> | null,
  lessonCweNumbers: Set<number> | null
) => {
  if (!cweDescriptions) return [];

  return Object.keys(cweDescriptions)
    .filter(number => !lessonCweNumbers || lessonCweNumbers.has(Number(number)))
    .map(number => {
      const name = cweDescriptions[number]?.Name;
      return {
        label: name ? `CWE-${number} (${name})` : `CWE-${number}`,
        value: `CWE-${number}`,
        sortKey: Number(number) || 0,
      };
    })
    .sort((left, right) => left.sortKey - right.sortKey);
};

const buildUserOptions = (users: DirectoryUserShape[]) =>
  users.map(user => {
    const given = user.name?.givenName || "";
    const family = user.name?.familyName || "";
    const display = `${given} ${family}`.trim();
    return {
      label: display ? `${display} (${user.email})` : (user.email ?? ""),
      value: user.email ?? "",
    };
  });

const fetchCwesWithLessons = async (cweNumbers: string[]) => {
  const chunks: string[][] = [];
  for (let index = 0; index < cweNumbers.length; index += CWE_TAGS_PER_REQUEST) {
    chunks.push(cweNumbers.slice(index, index + CWE_TAGS_PER_REQUEST));
  }

  const results = await Promise.all(
    chunks.map(chunk =>
      getContentClient()
        .getLessons({ tags: chunk.map(number => `cwe:${number}`) })
        .catch(() => [] as LessonDetailsPublic[])
    )
  );

  const cwesWithLessons = new Set<number>();
  results.flat().forEach(lesson => {
    const tags =
      "tags" in lesson && Array.isArray(lesson.tags)
        ? (lesson.tags as { category?: string; value?: string | number }[])
        : [];
    tags.forEach(tag => {
      const number = Number(tag.value);
      if (tag.category === "cwe" && !Number.isNaN(number)) {
        cwesWithLessons.add(number);
      }
    });
  });

  return cwesWithLessons;
};

interface CreatePlaDialogProps {
  visible: boolean;
  onHide: () => void;
  tenant: string;
  cweDescriptions: Record<string, { Name?: string; Description?: string }> | null;
  onCreated?: () => Promise<void> | void;
}

export default function CreatePlaDialog({
  visible,
  onHide,
  tenant,
  cweDescriptions,
  onCreated,
}: CreatePlaDialogProps) {
  const { isSjAdmin } = useUserContext();
  const { showToast } = useToast();
  const [users, setUsers] = useState<DirectoryUserShape[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [lessonCweNumbers, setLessonCweNumbers] = useState<Set<number> | null>(null);
  const [cweTagsLoading, setCweTagsLoading] = useState(false);
  const [selectedCwes, setSelectedCwes] = useState<string[]>([]);
  const [selectedEmail, setSelectedEmail] = useState<string | null>(null);
  const [gitRepo, setGitRepo] = useState("");
  const [userName, setUserName] = useState("");
  const [gitHeadSha, setGitHeadSha] = useState("");
  const [prNumber, setPrNumber] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resetForm = () => {
    setSelectedCwes([]);
    setSelectedEmail(null);
    setGitRepo("");
    setUserName("");
    setGitHeadSha("");
    setPrNumber(null);
  };

  useEffect(() => {
    if (!visible || !tenant) return;

    let cancelled = false;
    const loadUsers = async () => {
      setUsersLoading(true);
      try {
        const directoryResponse = await getDirectoryClient().getUsersForTenant(tenant, {
          includeArchived: false,
        });
        if (!cancelled) {
          setUsers((directoryResponse.data?.users ?? []) as DirectoryUserShape[]);
        }
      } catch (error) {
        console.error("Error fetching users for PLA dialog:", error);
        if (!cancelled) setUsers([]);
      } finally {
        if (!cancelled) setUsersLoading(false);
      }
    };

    void loadUsers();
    return () => {
      cancelled = true;
    };
  }, [tenant, visible]);

  useEffect(() => {
    if (!visible || !isSjAdmin || !cweDescriptions) return;

    let cancelled = false;
    const loadCwesWithLessons = async () => {
      setCweTagsLoading(true);
      try {
        const cwesWithLessons = await fetchCwesWithLessons(Object.keys(cweDescriptions));
        if (cancelled) return;

        if (cwesWithLessons.size > 0) {
          setLessonCweNumbers(cwesWithLessons);
        } else {
          showToast({
            severity: "warn",
            summary: "CWE lesson filter unavailable",
            detail: "Showing all CWEs; some may not have lessons.",
          });
        }
      } catch (error) {
        console.error("Error fetching lessons for CWE filter:", error);
        if (!cancelled) {
          showToast({
            severity: "warn",
            summary: "CWE lesson filter unavailable",
            detail: "Showing all CWEs; some may not have lessons.",
          });
        }
      } finally {
        if (!cancelled) setCweTagsLoading(false);
      }
    };

    void loadCwesWithLessons();
    return () => {
      cancelled = true;
    };
  }, [cweDescriptions, isSjAdmin, showToast, visible]);

  const cweOptions = useMemo(
    () => buildCweOptions(cweDescriptions, lessonCweNumbers),
    [cweDescriptions, lessonCweNumbers]
  );
  const userOptions = useMemo(() => buildUserOptions(users), [users]);

  const gitRepoError =
    gitRepo.length > 0 && gitRepo.length < MIN_TEXT_LEN
      ? `Must be at least ${MIN_TEXT_LEN} characters`
      : "";
  const userNameError =
    userName.length > 0 && userName.length < MIN_TEXT_LEN
      ? `Must be at least ${MIN_TEXT_LEN} characters`
      : "";
  const gitHeadShaError =
    gitHeadSha.length > 0 && !SHA_REGEX.test(gitHeadSha) ? "Must be a 40-character hex SHA" : "";
  const prNumberError =
    prNumber !== null && (!Number.isInteger(prNumber) || prNumber < 1)
      ? "Must be a positive integer"
      : "";

  const hasFieldErrors = Boolean(gitRepoError || userNameError || gitHeadShaError || prNumberError);
  const canSubmit = selectedCwes.length > 0 && !!selectedEmail && !isSubmitting && !hasFieldErrors;

  const handleSubmit = async () => {
    if (!canSubmit || !selectedEmail) return;

    setIsSubmitting(true);
    try {
      const payload: RecordCWERequest = {
        cwes: selectedCwes,
        gitCommitterEmail: selectedEmail,
        gitRepo: gitRepo || DEFAULT_GIT_REPO,
        username: userName || selectedEmail.split("@")[0] || selectedEmail,
        gitHeadSha: gitHeadSha || randomHex40(),
        prNumber: prNumber ?? randomPrNumber(),
      };

      await getIntegrationsClient().recordCWEs(payload).promise;

      showToast({
        severity: "success",
        summary: "PLA created",
        detail: `Recorded ${payload.cwes?.length ?? 0} CWE${(payload.cwes?.length ?? 0) === 1 ? "" : "s"} for ${payload.gitCommitterEmail}. It may take a few minutes for the new PLA to appear in the table.`,
        life: 10000,
      });

      await onCreated?.();
      resetForm();
      onHide();
    } catch (error) {
      console.error("Error creating PLA:", error);
      showToast({
        severity: "error",
        summary: "Failed to create PLA",
        detail: error instanceof Error ? error.message : "Unable to record CWEs.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleHide = () => {
    if (isSubmitting) return;
    resetForm();
    onHide();
  };

  return (
    <Dialog
      header="Create PLA"
      visible={visible}
      onHide={handleHide}
      style={{ width: "560px" }}
      modal
      footer={
        <div className="flex justify-end gap-2">
          <Button
            label="Cancel"
            severity="secondary"
            onClick={handleHide}
            disabled={isSubmitting}
          />
          <Button
            label="Create"
            icon="pi pi-check"
            onClick={() => void handleSubmit()}
            loading={isSubmitting}
            disabled={!canSubmit}
          />
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <label htmlFor="create-pla-cwes" className="font-semibold">
            CWEs <span className="text-red-500">*</span>
          </label>
          <MultiSelect
            inputId="create-pla-cwes"
            value={selectedCwes}
            options={cweOptions}
            onChange={event => setSelectedCwes(event.value || [])}
            placeholder={
              cweTagsLoading || cweOptions.length === 0 ? "Loading CWEs..." : "Select CWEs"
            }
            filter
            display="chip"
            maxSelectedLabels={3}
            disabled={cweTagsLoading || cweOptions.length === 0}
            className="w-full"
            panelStyle={{ maxWidth: "650px" }}
          />
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="create-pla-email" className="font-semibold">
            Email Address <span className="text-red-500">*</span>
          </label>
          <Dropdown
            inputId="create-pla-email"
            value={selectedEmail}
            options={userOptions}
            onChange={event => setSelectedEmail(event.value as string)}
            placeholder={usersLoading ? "Loading users..." : "Select a user"}
            filter
            filterInputAutoFocus
            disabled={usersLoading}
            className="w-full"
            panelStyle={{ maxWidth: "650px" }}
          />
        </div>

        <Panel header="Advanced (optional)" toggleable collapsed>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <label htmlFor="create-pla-git-repo" className="font-semibold">
                Git Repo
              </label>
              <InputText
                id="create-pla-git-repo"
                value={gitRepo}
                onChange={event => setGitRepo(event.target.value)}
                placeholder={DEFAULT_GIT_REPO}
                className={gitRepoError ? "p-invalid" : ""}
              />
              {gitRepoError && <small className="p-error">{gitRepoError}</small>}
            </div>

            <div className="flex flex-col gap-1">
              <label htmlFor="create-pla-username" className="font-semibold">
                Username
              </label>
              <InputText
                id="create-pla-username"
                value={userName}
                onChange={event => setUserName(event.target.value)}
                placeholder={
                  selectedEmail ? selectedEmail.split("@")[0] : "Defaults to email prefix"
                }
                className={userNameError ? "p-invalid" : ""}
              />
              {userNameError && <small className="p-error">{userNameError}</small>}
            </div>

            <div className="flex flex-col gap-1">
              <label htmlFor="create-pla-sha" className="font-semibold">
                Git Head SHA
              </label>
              <InputText
                id="create-pla-sha"
                value={gitHeadSha}
                onChange={event => setGitHeadSha(event.target.value)}
                placeholder="40-char hex (random if blank)"
                className={gitHeadShaError ? "p-invalid" : ""}
              />
              {gitHeadShaError && <small className="p-error">{gitHeadShaError}</small>}
            </div>

            <div className="flex flex-col gap-1">
              <label htmlFor="create-pla-pr" className="font-semibold">
                PR Number
              </label>
              <InputNumber
                inputId="create-pla-pr"
                value={prNumber}
                onValueChange={event => setPrNumber((event.value as number | null) ?? null)}
                min={1}
                useGrouping={false}
                placeholder="Random 1–1000 if blank"
                className={prNumberError ? "p-invalid" : ""}
              />
              {prNumberError && <small className="p-error">{prNumberError}</small>}
            </div>
          </div>
        </Panel>
      </div>
    </Dialog>
  );
}
